import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Card } from '../ui/Card';
import { Building2, Link as LinkIcon, Bot, Key, CreditCard, CheckCircle2, ChevronRight, ChevronLeft, Activity, SearchCheck, BarChart3, Lock, FileText, Rocket, Palette, Blocks, Copy, Check, Shield, AlertTriangle } from 'lucide-react';
interface OnboardingPanelProps {
  onComplete: () => void;
}
const TOTAL_STEPS = 6;
const steps = [{
  id: 1,
  label: 'Empresa',
  icon: Building2,
  color: 'text-violet-400'
}, {
  id: 2,
  label: 'Integraciones',
  icon: LinkIcon,
  color: 'text-blue-400'
}, {
  id: 3,
  label: 'Agentes',
  icon: Bot,
  color: 'text-emerald-400'
}, {
  id: 4,
  label: 'Pago',
  icon: CreditCard,
  color: 'text-amber-400'
}, {
  id: 5,
  label: 'API Key',
  icon: Key,
  color: 'text-cyan-400'
}, {
  id: 6,
  label: 'Listo',
  icon: CheckCircle2,
  color: 'text-emerald-400'
}];
const agentsList = [{
  id: 'scrum',
  name: 'Scrum Master Pro',
  specialty: 'Agile & Sprints',
  icon: Activity,
  color: 'text-emerald-400',
  price: 0.02,
  status: 'available'
}, {
  id: 'qa',
  name: 'QA Inspector',
  specialty: 'Testing & Bugs',
  icon: SearchCheck,
  color: 'text-rose-400',
  price: 0.03,
  status: 'available'
}, {
  id: 'analytics',
  name: 'Data Analyst',
  specialty: 'Metrics & BI',
  icon: BarChart3,
  color: 'text-cyan-400',
  price: 0.02,
  status: 'available'
}, {
  id: 'security',
  name: 'Security Guardian',
  specialty: 'Compliance',
  icon: Lock,
  color: 'text-emerald-400',
  price: 0.05,
  status: 'busy'
}, {
  id: 'docs',
  name: 'Doc Writer',
  specialty: 'Knowledge Base',
  icon: FileText,
  color: 'text-amber-400',
  price: 0.01,
  status: 'available'
}, {
  id: 'devops',
  name: 'DevOps Engineer',
  specialty: 'CI/CD & Infra',
  icon: Rocket,
  color: 'text-orange-400',
  price: 0.04,
  status: 'available'
}, {
  id: 'ux',
  name: 'UX Reviewer',
  specialty: 'Design & A11y',
  icon: Palette,
  color: 'text-violet-400',
  price: 0.03,
  status: 'coming_soon'
}, {
  id: 'architect',
  name: 'System Architect',
  specialty: 'Architecture',
  icon: Blocks,
  color: 'text-sky-400',
  price: 0.06,
  status: 'busy'
}];
// Auto-format card number: 1234567890123456 → 1234 5678 9012 3456
function formatCardNumber(val: string) {
  const digits = val.replace(/\D/g, '').slice(0, 16);
  return digits.replace(/(.{4})/g, '$1 ').trim();
}
// Auto-format expiry: 1234 → 12/34
function formatExpiry(val: string) {
  const digits = val.replace(/\D/g, '').slice(0, 4);
  if (digits.length > 2) return digits.slice(0, 2) + '/' + digits.slice(2);
  return digits;
}
// Detect card brand from first digits
function detectCardBrand(num: string): string {
  const d = num.replace(/\D/g, '');
  if (d.startsWith('4')) return 'VISA';
  if (d.startsWith('5') || d.startsWith('2')) return 'MC';
  if (d.startsWith('3')) return 'AMEX';
  return '';
}
export function OnboardingPanel({
  onComplete
}: OnboardingPanelProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [direction, setDirection] = useState(1); // 1 = forward, -1 = back
  // Step 1: Company
  const [companyName, setCompanyName] = useState('');
  const [industry, setIndustry] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [methodology, setMethodology] = useState('Scrum');
  // Step 2: Integrations
  const [integrations, setIntegrations] = useState({
    jira: true,
    confluence: false,
    slack: false,
    github: false
  });
  const [testingIntegrations, setTestingIntegrations] = useState(false);
  const [integrationResults, setIntegrationResults] = useState<Record<string, boolean>>({});
  // Step 3: Agents
  const [selectedAgents, setSelectedAgents] = useState<string[]>(['scrum']);
  // Step 4: Payment
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvc, setCardCvc] = useState('');
  const [cardName, setCardName] = useState('');
  const [skippedPayment, setSkippedPayment] = useState(false);
  // Step 5: API Key
  const [copied, setCopied] = useState(false);
  const apiKey = 'vsm_sk_live_9f8a7b6c5d4e3f2a1b0c9d8e7f6a5b4c';
  const goNext = () => {
    setDirection(1);
    setCurrentStep(prev => Math.min(prev + 1, TOTAL_STEPS));
  };
  const goBack = () => {
    setDirection(-1);
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };
  const testConnections = () => {
    setTestingIntegrations(true);
    setTimeout(() => {
      setTestingIntegrations(false);
      setIntegrationResults({
        jira: true,
        confluence: true
      });
    }, 2000);
  };
  const toggleAgent = (id: string, status: string) => {
    if (status === 'coming_soon') return;
    setSelectedAgents(prev => prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]);
  };
  const copyApiKey = () => {
    navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const cardBrand = detectCardBrand(cardNumber);
  const isPaymentValid = cardNumber.replace(/\D/g, '').length >= 15 && cardExpiry.length >= 4 && cardCvc.length >= 3 && cardName.length >= 2;
  const slideVariants = {
    enter: (dir: number) => ({
      opacity: 0,
      x: dir > 0 ? 30 : -30
    }),
    center: {
      opacity: 1,
      x: 0
    },
    exit: (dir: number) => ({
      opacity: 0,
      x: dir > 0 ? -30 : 30
    })
  };
  return <div className="h-full overflow-y-auto p-4 lg:p-8 animate-in fade-in duration-300 bg-slate-950 text-slate-200 flex flex-col items-center" data-id="element-5707">
      {/* Progress Bar */}
      <div className="w-full max-w-3xl mb-10 mt-4" data-id="element-5708">
        <div className="flex items-center justify-between relative" data-id="element-5709">
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-0.5 bg-slate-800 -z-10" data-id="element-5710" />
          <div className="absolute left-0 top-1/2 -translate-y-1/2 h-0.5 bg-violet-500 -z-10 transition-all duration-500" style={{
          width: `${(currentStep - 1) / (TOTAL_STEPS - 1) * 100}%`
        }} data-id="element-5711" />

          {steps.map(step => {
          const isCompleted = currentStep > step.id;
          const isCurrent = currentStep === step.id;
          return <div key={step.id} className="flex flex-col items-center gap-2" data-id="element-5712">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition-colors duration-300 text-sm ${isCompleted ? 'bg-violet-500 border-violet-500 text-white' : isCurrent ? 'bg-slate-900 border-violet-500 text-violet-400' : 'bg-slate-900 border-slate-700 text-slate-500'}`} data-id="element-5713">
                  {isCompleted ? <CheckCircle2 size={18} data-id="element-5714" /> : <span data-id="element-5715">{step.id}</span>}
                </div>
                <span className={`text-[10px] font-medium hidden sm:block ${isCurrent ? 'text-slate-200' : 'text-slate-500'}`} data-id="element-5716">
                  {step.label}
                </span>
              </div>;
        })}
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full max-w-2xl relative min-h-[520px]" data-id="element-5717">
        <AnimatePresence mode="wait" custom={direction} data-id="element-5718">
          {/* STEP 1: EMPRESA */}
          {currentStep === 1 && <motion.div key="step1" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          duration: 0.2
        }} className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-xl" data-id="element-5719">
              <div className="flex items-center gap-4 mb-8" data-id="element-5720">
                <div className="w-12 h-12 rounded-full bg-violet-500/20 flex items-center justify-center text-violet-400" data-id="element-5721">
                  <Building2 size={24} data-id="element-5722" />
                </div>
                <div data-id="element-5723">
                  <h2 className="text-2xl font-bold text-slate-100" data-id="element-5724">
                    Configurá tu Empresa
                  </h2>
                  <p className="text-slate-400" data-id="element-5725">
                    Contanos sobre tu equipo para adaptar los agentes.
                  </p>
                </div>
              </div>

              <div className="space-y-6" data-id="element-5726">
                <div data-id="element-5727">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-5728">
                    Nombre de la Empresa
                  </label>
                  <input value={companyName} onChange={e => setCompanyName(e.target.value)} className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 text-slate-200 focus:outline-none focus:border-violet-500" placeholder="Ej: Acme Corp" data-id="element-5729" />
                </div>
                <div className="grid grid-cols-2 gap-4" data-id="element-5730">
                  <div data-id="element-5731">
                    <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-5732">
                      Industria
                    </label>
                    <select value={industry} onChange={e => setIndustry(e.target.value)} className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 text-slate-200 focus:outline-none focus:border-violet-500 appearance-none" data-id="element-5733">
                      <option value="" data-id="element-5734">Seleccionar...</option>
                      <option value="tech" data-id="element-5735">Tecnología</option>
                      <option value="fin" data-id="element-5736">Finanzas</option>
                      <option value="logistics" data-id="element-5737">Logística</option>
                      <option value="health" data-id="element-5738">Salud</option>
                      <option value="legal" data-id="element-5739">Legal</option>
                      <option value="retail" data-id="element-5740">Retail</option>
                      <option value="other" data-id="element-5741">Otro</option>
                    </select>
                  </div>
                  <div data-id="element-5742">
                    <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-5743">
                      Metodología
                    </label>
                    <select value={methodology} onChange={e => setMethodology(e.target.value)} className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 text-slate-200 focus:outline-none focus:border-violet-500 appearance-none" data-id="element-5744">
                      <option value="Scrum" data-id="element-5745">Scrum</option>
                      <option value="Kanban" data-id="element-5746">Kanban</option>
                      <option value="SAFe" data-id="element-5747">SAFe</option>
                      <option value="Custom" data-id="element-5748">Custom</option>
                    </select>
                  </div>
                </div>
                <div data-id="element-5749">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-5750">
                    Tamaño del Equipo
                  </label>
                  <div className="grid grid-cols-4 gap-3" data-id="element-5751">
                    {['1-5', '6-20', '21-50', '50+'].map(size => <button key={size} onClick={() => setTeamSize(size)} className={`py-2 rounded-lg border text-sm font-medium transition-all ${teamSize === size ? 'bg-violet-500/20 border-violet-500 text-violet-300' : 'bg-slate-950 border-slate-700 text-slate-400 hover:border-slate-600'}`} data-id="element-5752">
                        {size}
                      </button>)}
                  </div>
                </div>
              </div>

              <div className="mt-10 flex justify-end" data-id="element-5753">
                <Button onClick={goNext} disabled={!companyName || !industry} className="bg-violet-600 hover:bg-violet-500 px-8" data-id="element-5754">
                  Continuar <ChevronRight size={16} className="ml-2" data-id="element-5755" />
                </Button>
              </div>
            </motion.div>}

          {/* STEP 2: INTEGRACIONES */}
          {currentStep === 2 && <motion.div key="step2" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          duration: 0.2
        }} className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-xl" data-id="element-5756">
              <div className="flex items-center gap-4 mb-8" data-id="element-5757">
                <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400" data-id="element-5758">
                  <LinkIcon size={24} data-id="element-5759" />
                </div>
                <div data-id="element-5760">
                  <h2 className="text-2xl font-bold text-slate-100" data-id="element-5761">
                    Conectá tus herramientas
                  </h2>
                  <p className="text-slate-400" data-id="element-5762">
                    VSM necesita acceso a tu plataforma para operar.
                  </p>
                </div>
              </div>

              <div className="space-y-3 mb-8" data-id="element-5763">
                {/* Jira */}
                <div className={`border rounded-xl p-4 transition-all ${integrations.jira ? 'bg-slate-950 border-blue-500/50' : 'bg-slate-950/50 border-slate-800'}`} data-id="element-5764">
                  <div className="flex items-center justify-between mb-2" data-id="element-5765">
                    <div className="flex items-center gap-3" data-id="element-5766">
                      <div className="w-8 h-8 rounded bg-blue-900/30 flex items-center justify-center text-blue-400" data-id="element-5767">
                        <Activity size={16} data-id="element-5768" />
                      </div>
                      <p className="font-semibold text-slate-200 flex items-center gap-2" data-id="element-5769">
                        Jira Software{' '}
                        <Badge className="bg-blue-500/20 text-blue-300 border-0 text-[10px]" data-id="element-5770">
                          Recomendado
                        </Badge>
                      </p>
                    </div>
                    <button onClick={() => setIntegrations(p => ({
                  ...p,
                  jira: !p.jira
                }))} className={`w-10 h-5 rounded-full relative transition-colors ${integrations.jira ? 'bg-blue-500' : 'bg-slate-700'}`} data-id="element-5771">
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${integrations.jira ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-5772" />
                    </button>
                  </div>
                  {integrations.jira && <div className="mt-3 space-y-3 pt-3 border-t border-slate-800" data-id="element-5773">
                      <input className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500" placeholder="Base URL (ej: https://acme.atlassian.net)" data-id="element-5774" />

                      <div className="grid grid-cols-2 gap-3" data-id="element-5775">
                        <input className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500" placeholder="Email" data-id="element-5776" />

                        <input type="password" className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500" placeholder="API Token" data-id="element-5777" />
                      </div>
                    </div>}
                </div>

                {/* Confluence */}
                <div className={`border rounded-xl p-4 transition-all ${integrations.confluence ? 'bg-slate-950 border-blue-500/50' : 'bg-slate-950/50 border-slate-800'}`} data-id="element-5778">
                  <div className="flex items-center justify-between" data-id="element-5779">
                    <div className="flex items-center gap-3" data-id="element-5780">
                      <div className="w-8 h-8 rounded bg-sky-900/30 flex items-center justify-center text-sky-400" data-id="element-5781">
                        <FileText size={16} data-id="element-5782" />
                      </div>
                      <p className="font-semibold text-slate-200" data-id="element-5783">Confluence</p>
                    </div>
                    <button onClick={() => setIntegrations(p => ({
                  ...p,
                  confluence: !p.confluence
                }))} className={`w-10 h-5 rounded-full relative transition-colors ${integrations.confluence ? 'bg-blue-500' : 'bg-slate-700'}`} data-id="element-5784">
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${integrations.confluence ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-5785" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between" data-id="element-5786">
                <div className="flex gap-3" data-id="element-5787">
                  <Button variant="outline" size="sm" onClick={goBack} className="border-slate-700 text-slate-400" data-id="element-5788">
                    <ChevronLeft size={16} data-id="element-5789" />
                  </Button>
                  <Button variant="outline" onClick={testConnections} isLoading={testingIntegrations} className="border-slate-700 text-slate-300" data-id="element-5790">
                    Probar Conexiones
                  </Button>
                </div>
                <div className="flex gap-4 items-center" data-id="element-5791">
                  <button onClick={goNext} className="text-sm text-slate-500 hover:text-slate-300" data-id="element-5792">
                    Omitir
                  </button>
                  <Button onClick={goNext} className="bg-blue-600 hover:bg-blue-500 px-8" data-id="element-5793">
                    Continuar <ChevronRight size={16} className="ml-2" data-id="element-5794" />
                  </Button>
                </div>
              </div>
            </motion.div>}

          {/* STEP 3: AGENTES */}
          {currentStep === 3 && <motion.div key="step3" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          duration: 0.2
        }} className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-xl" data-id="element-5795">
              <div className="flex items-center gap-4 mb-6" data-id="element-5796">
                <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400" data-id="element-5797">
                  <Bot size={24} data-id="element-5798" />
                </div>
                <div data-id="element-5799">
                  <h2 className="text-2xl font-bold text-slate-100" data-id="element-5800">
                    Armá tu equipo de agentes IA
                  </h2>
                  <p className="text-slate-400" data-id="element-5801">
                    Seleccioná los especialistas que necesitás.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-6 max-h-[280px] overflow-y-auto pr-2" data-id="element-5802">
                {agentsList.map(agent => {
              const Icon = agent.icon;
              const isSelected = selectedAgents.includes(agent.id);
              const isComingSoon = agent.status === 'coming_soon';
              return <div key={agent.id} onClick={() => toggleAgent(agent.id, agent.status)} className={`p-3 rounded-xl border cursor-pointer transition-all ${isComingSoon ? 'opacity-50 grayscale cursor-not-allowed bg-slate-950/50 border-slate-800' : isSelected ? 'bg-emerald-950/20 border-emerald-500/50 ring-1 ring-emerald-500/30' : 'bg-slate-950 border-slate-800 hover:border-slate-700'}`} data-id="element-5803">
                      <div className="flex items-start gap-3" data-id="element-5804">
                        <div className="w-8 h-8 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center shrink-0" data-id="element-5805">
                          <Icon size={14} className={agent.color} data-id="element-5806" />
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-5807">
                          <p className="text-sm font-bold text-slate-200 truncate" data-id="element-5808">
                            {agent.name}
                          </p>
                          <p className="text-[10px] text-slate-400 truncate mb-1" data-id="element-5809">
                            {agent.specialty}
                          </p>
                          <div className="flex items-center justify-between mt-2" data-id="element-5810">
                            <span className="text-xs font-mono text-slate-300" data-id="element-5811">
                              ${agent.price}/msg
                            </span>
                            {isComingSoon ? <span className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold" data-id="element-5812">
                                Pronto
                              </span> : isSelected ? <span className="text-[10px] bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded font-medium" data-id="element-5813">
                                Contratado
                              </span> : <span className="text-[10px] bg-slate-800 text-slate-400 px-2 py-0.5 rounded font-medium" data-id="element-5814">
                                Contratar
                              </span>}
                          </div>
                        </div>
                      </div>
                    </div>;
            })}
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-slate-800" data-id="element-5815">
                <div className="flex items-center gap-3" data-id="element-5816">
                  <Button variant="outline" size="sm" onClick={goBack} className="border-slate-700 text-slate-400" data-id="element-5817">
                    <ChevronLeft size={16} data-id="element-5818" />
                  </Button>
                  <p className="text-sm text-slate-400" data-id="element-5819">
                    <strong className="text-slate-200" data-id="element-5820">
                      {selectedAgents.length}
                    </strong>{' '}
                    agentes seleccionados
                  </p>
                </div>
                <Button onClick={goNext} disabled={selectedAgents.length === 0} className="bg-emerald-600 hover:bg-emerald-500 px-8" data-id="element-5821">
                  Continuar <ChevronRight size={16} className="ml-2" data-id="element-5822" />
                </Button>
              </div>
            </motion.div>}

          {/* STEP 4: MÉTODO DE PAGO */}
          {currentStep === 4 && <motion.div key="step4" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          duration: 0.2
        }} className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-xl" data-id="element-5823">
              <div className="flex items-center gap-4 mb-8" data-id="element-5824">
                <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400" data-id="element-5825">
                  <CreditCard size={24} data-id="element-5826" />
                </div>
                <div data-id="element-5827">
                  <h2 className="text-2xl font-bold text-slate-100" data-id="element-5828">
                    Método de Pago
                  </h2>
                  <p className="text-slate-400" data-id="element-5829">
                    Agregá una tarjeta para cuando superes el free tier.
                  </p>
                </div>
              </div>

              <div className="space-y-5" data-id="element-5830">
                {/* Card Number */}
                <div data-id="element-5831">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-5832">
                    Número de Tarjeta
                  </label>
                  <div className="relative" data-id="element-5833">
                    <input value={formatCardNumber(cardNumber)} onChange={e => setCardNumber(e.target.value.replace(/\D/g, '').slice(0, 16))} className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 pr-16 text-slate-200 font-mono tracking-wider focus:outline-none focus:border-amber-500" placeholder="4242 4242 4242 4242" maxLength={19} data-id="element-5834" />

                    {cardBrand && <span className="absolute right-3 top-1/2 -translate-y-1/2 px-2 py-0.5 bg-slate-800 rounded text-[10px] font-bold text-white border border-slate-700" data-id="element-5835">
                        {cardBrand}
                      </span>}
                  </div>
                </div>

                {/* Expiry + CVC */}
                <div className="grid grid-cols-2 gap-4" data-id="element-5836">
                  <div data-id="element-5837">
                    <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-5838">
                      Vencimiento
                    </label>
                    <input value={formatExpiry(cardExpiry)} onChange={e => setCardExpiry(e.target.value.replace(/\D/g, '').slice(0, 4))} className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 text-slate-200 font-mono focus:outline-none focus:border-amber-500" placeholder="MM/YY" maxLength={5} data-id="element-5839" />
                  </div>
                  <div data-id="element-5840">
                    <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-5841">
                      CVC
                    </label>
                    <input value={cardCvc} onChange={e => setCardCvc(e.target.value.replace(/\D/g, '').slice(0, 4))} type="password" className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 text-slate-200 font-mono focus:outline-none focus:border-amber-500" placeholder="•••" maxLength={4} data-id="element-5842" />
                  </div>
                </div>

                {/* Cardholder Name */}
                <div data-id="element-5843">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-5844">
                    Nombre en la Tarjeta
                  </label>
                  <input value={cardName} onChange={e => setCardName(e.target.value)} className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 text-slate-200 focus:outline-none focus:border-amber-500" placeholder="Como aparece en la tarjeta" data-id="element-5845" />
                </div>

                {/* Security Badges */}
                <div className="flex items-center gap-3" data-id="element-5846">
                  <span className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-950/30 border border-emerald-900/40 rounded-lg text-[10px] font-medium text-emerald-400" data-id="element-5847">
                    <Shield size={10} data-id="element-5848" /> Encriptado SSL
                  </span>
                  <span className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-950/30 border border-emerald-900/40 rounded-lg text-[10px] font-medium text-emerald-400" data-id="element-5849">
                    <Lock size={10} data-id="element-5850" /> PCI Compliant
                  </span>
                </div>

                <p className="text-xs text-slate-500 flex items-center gap-1.5" data-id="element-5851">
                  <AlertTriangle size={10} className="text-amber-500" data-id="element-5852" />
                  No se realizará ningún cobro hasta que superes los {500}{' '}
                  mensajes gratis del mes.
                </p>
              </div>

              <div className="mt-8 flex items-center justify-between" data-id="element-5853">
                <Button variant="outline" size="sm" onClick={goBack} className="border-slate-700 text-slate-400" data-id="element-5854">
                  <ChevronLeft size={16} data-id="element-5855" />
                </Button>
                <div className="flex gap-4 items-center" data-id="element-5856">
                  <button onClick={() => {
                setSkippedPayment(true);
                goNext();
              }} className="text-sm text-slate-500 hover:text-slate-300" data-id="element-5857">
                    Omitir por ahora
                  </button>
                  <Button onClick={() => {
                setSkippedPayment(false);
                goNext();
              }} disabled={!isPaymentValid} className="bg-amber-600 hover:bg-amber-500 px-8" data-id="element-5858">
                    Guardar y Continuar{' '}
                    <ChevronRight size={16} className="ml-2" data-id="element-5859" />
                  </Button>
                </div>
              </div>
            </motion.div>}

          {/* STEP 5: API KEY */}
          {currentStep === 5 && <motion.div key="step5" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          duration: 0.2
        }} className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-xl" data-id="element-5860">
              <div className="flex items-center gap-4 mb-8" data-id="element-5861">
                <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-400" data-id="element-5862">
                  <Key size={24} data-id="element-5863" />
                </div>
                <div data-id="element-5864">
                  <h2 className="text-2xl font-bold text-slate-100" data-id="element-5865">
                    Tu API Key está lista
                  </h2>
                  <p className="text-slate-400" data-id="element-5866">
                    Usá esta key para conectar tus propios sistemas a VSM.
                  </p>
                </div>
              </div>

              <div className="bg-slate-950 border border-slate-800 rounded-xl p-4 mb-6 flex items-center justify-between" data-id="element-5867">
                <code className="text-cyan-400 font-mono text-sm truncate" data-id="element-5868">
                  {apiKey}
                </code>
                <button onClick={copyApiKey} className="p-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-slate-400 transition-colors shrink-0 ml-3" data-id="element-5869">
                  {copied ? <Check size={16} className="text-emerald-400" data-id="element-5870" /> : <Copy size={16} data-id="element-5871" />}
                </button>
              </div>

              <div className="bg-[#0d1117] rounded-xl overflow-hidden border border-slate-800 mb-8" data-id="element-5872">
                <div className="px-4 py-2 bg-slate-900/50 border-b border-slate-800 text-xs text-slate-500 font-mono" data-id="element-5873">
                  Ejemplo de uso (cURL)
                </div>
                <pre className="p-4 text-xs font-mono overflow-x-auto leading-relaxed" data-id="element-5874">
                  <span className="text-pink-400" data-id="element-5875">curl</span>{' '}
                  <span className="text-slate-300" data-id="element-5876">
                    -X POST https://api.vsm.ai/v1/chat \
                  </span>
                  {'\n'}
                  <span className="text-slate-300" data-id="element-5877"> -H </span>
                  <span className="text-green-400" data-id="element-5878">
                    "Authorization: Bearer {apiKey.slice(0, 12)}..."
                  </span>
                  <span className="text-slate-300" data-id="element-5879"> \</span>
                  {'\n'}
                  <span className="text-slate-300" data-id="element-5880"> -H </span>
                  <span className="text-green-400" data-id="element-5881">
                    "Content-Type: application/json"
                  </span>
                  <span className="text-slate-300" data-id="element-5882"> \</span>
                  {'\n'}
                  <span className="text-slate-300" data-id="element-5883"> -d </span>
                  <span className="text-yellow-300" data-id="element-5884">
                    {'\'{"message": "¿Cuántos bugs hay?", "agent": "scrum-master"}\''}
                  </span>
                </pre>
              </div>

              <p className="text-xs text-slate-500 mb-6" data-id="element-5885">
                Podés generar más keys desde Configuración → Cuenta.
              </p>

              <div className="flex items-center justify-between" data-id="element-5886">
                <Button variant="outline" size="sm" onClick={goBack} className="border-slate-700 text-slate-400" data-id="element-5887">
                  <ChevronLeft size={16} data-id="element-5888" />
                </Button>
                <Button onClick={goNext} className="bg-cyan-600 hover:bg-cyan-500 px-8" data-id="element-5889">
                  Continuar <ChevronRight size={16} className="ml-2" data-id="element-5890" />
                </Button>
              </div>
            </motion.div>}

          {/* STEP 6: SUCCESS */}
          {currentStep === 6 && <motion.div key="step6" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="bg-slate-900 border border-slate-800 rounded-2xl p-10 shadow-xl text-center" data-id="element-5891">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-24 h-24 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 mx-auto mb-6 relative" data-id="element-5892">
                <div className="absolute inset-0 rounded-full bg-emerald-400/20 animate-ping" data-id="element-5893" />
                <CheckCircle2 size={48} data-id="element-5894" />
              </motion.div>

              <h2 className="text-3xl font-bold text-slate-100 mb-2" data-id="element-5895">
                ¡Todo Listo!
              </h2>
              <p className="text-slate-400 mb-8 max-w-sm mx-auto" data-id="element-5896">
                Tu workspace está configurado y los agentes están listos para
                trabajar.
              </p>

              <div className="bg-slate-950 border border-slate-800 rounded-xl p-6 text-left max-w-sm mx-auto mb-8 space-y-3" data-id="element-5897">
                {[{
              delay: 0.2,
              text: <>
                        Empresa configurada:{' '}
                        <strong className="text-slate-100" data-id="element-5898">
                          {companyName || 'Workium AI'}
                        </strong>
                      </>,
              ok: true
            }, {
              delay: 0.3,
              text: 'Jira conectado',
              ok: integrations.jira
            }, {
              delay: 0.4,
              text: <>
                        <strong className="text-slate-100" data-id="element-5899">
                          {selectedAgents.length}
                        </strong>{' '}
                        agentes contratados
                      </>,
              ok: true
            }, {
              delay: 0.5,
              text: skippedPayment ? 'Pago pendiente — configuralo desde Facturación' : 'Método de pago verificado',
              ok: !skippedPayment
            }, {
              delay: 0.6,
              text: 'API Key generada',
              ok: true
            }].map((item, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: item.delay
            }} className="flex items-center gap-3 text-sm" data-id="element-5900">
                    {item.ok ? <CheckCircle2 size={16} className="text-emerald-400 shrink-0" data-id="element-5901" /> : <AlertTriangle size={16} className="text-amber-400 shrink-0" data-id="element-5902" />}
                    <span className={item.ok ? 'text-slate-300' : 'text-amber-300'} data-id="element-5903">
                      {item.text}
                    </span>
                  </motion.div>)}
              </div>

              <div className="flex flex-col gap-3 max-w-sm mx-auto" data-id="element-5904">
                <Button onClick={onComplete} className="w-full bg-emerald-600 hover:bg-emerald-500 py-6 text-lg" data-id="element-5905">
                  Ir al Dashboard
                </Button>
                <button className="text-sm text-slate-500 hover:text-slate-300 transition-colors" data-id="element-5906">
                  Explorar la documentación
                </button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}